// client/src/components/GoogleTranslateWidget.js
import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useGeo } from '../context/GeoContext';

// Google's actual supported translate codes, restricted to languages likely
// to matter for this site's visitors: South Africa's 11 official languages
// plus the languages most common among the rest of our international traffic.
// Anything outside this list is left as English rather than guessed at.
const SUPPORTED_LANGS = [
  'en', 'af', 'zu', 'xh', 'st', 'tn', 've', 'ts', 'ss', 'nr', 'nso',
  'fr', 'pt', 'de', 'es', 'nl', 'it', 'zh-CN', 'ar', 'hi', 'sw',
];

// Fallback only - used when the visitor's country isn't in our language map
// (GeoContext), e.g. an unmapped country whose browser happens to be set to
// a language we do support.
const detectBrowserLangCode = () => {
  const primary = (navigator.language || 'en').split('-')[0].toLowerCase();
  return SUPPORTED_LANGS.includes(primary) ? primary : null;
};

const getGoogTransCookie = () => {
  const match = document.cookie.match(/(?:^|; )googtrans=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const GoogleTranslateWidget = ({ sx }) => {
  const { ready, targetLanguage } = useGeo();
  const handled = useRef(false);

  useEffect(() => {
    if (!ready || handled.current) return;
    handled.current = true;

    // Fully automatic, based on the visitor's actual country (falling back
    // to browser language only if their country isn't one we've mapped) -
    // never something they have to pick. Google only picks up the googtrans
    // cookie correctly if it's present from the very start of a page load,
    // so a visitor whose language we haven't applied yet gets one silent,
    // immediate reload (guarded against looping if it somehow doesn't take).
    const target = targetLanguage || detectBrowserLangCode();
    const alreadyApplied = getGoogTransCookie() === `/en/${target}`;
    // Keyed to the specific target, not just "did we ever reload this
    // session" - a visitor whose apparent country changes mid-session (VPN,
    // switching networks) should still get a fresh reload for the new
    // target, while a same-target retry loop is still prevented.
    const lastReloadAttemptFor = sessionStorage.getItem('translateAutoReloadFor');
    if (target && target !== 'en' && !alreadyApplied && lastReloadAttemptFor !== target) {
      sessionStorage.setItem('translateAutoReloadFor', target);
      document.cookie = `googtrans=/en/${target};path=/`;
      window.location.reload();
      return; // don't bother loading the widget script - we're reloading now
    }

    window.googleTranslateElementInit = () => {
      // eslint-disable-next-line no-undef
      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'en',
          autoDisplay: false,
          // eslint-disable-next-line no-undef
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        },
        'google_translate_element'
      );
    };

    if (!document.querySelector('script[src*="translate.google.com"]')) {
      const script = document.createElement('script');
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [ready, targetLanguage]);

  return <Box id="google_translate_element" sx={{ display: 'flex', alignItems: 'center', ...sx }} />;
};

export default GoogleTranslateWidget;
